import React, { useEffect, useRef } from 'react';

// 4x4 Bayer matrix, normalised to (0,1): classic ordered dithering.
const BAYER = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]].map((r) => r.map((v) => (v + 0.5) / 16));

// ponytail: sum-of-sines "noise". Swap for simplex if the blob ever reads as too regular.
const field = (x, y, t) =>
  (Math.sin(x * 1.7 + t * 0.9) * Math.cos(y * 1.3 - t * 0.7) +
    Math.sin((x + y) * 2.3 - t * 0.5) * 0.5 +
    Math.cos(x * 3.1 - y * 2.2 + t * 1.3) * 0.25) / 1.75;

/** A breathing organic form rendered as a halftone dot grid; the cursor erodes it. */
export default function Dither({ cell = 9, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ink = getComputedStyle(canvas).color;
    const target = { x: -1e4, y: -1e4 };
    const mouse = { x: -1e4, y: -1e4 };
    let w = 0, h = 0, cols = 0, rows = 0, raf = 0, visible = true;
    const t0 = performance.now();

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / cell); rows = Math.ceil(h / cell);
    };

    const draw = (now) => {
      const t = (now - t0) / 1000;
      mouse.x += (target.x - mouse.x) * 0.12;
      mouse.y += (target.y - mouse.y) * 0.12;
      const cx = cols / 2, cy = rows / 2;
      const R = Math.min(cols * 0.42, rows * 0.72); // blob radius in cells
      const erode = 80 / cell; // cursor radius in cells
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = ink;
      ctx.beginPath();
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const nx = (i - cx) / R, ny = (j - cy) / R;
          const d = Math.hypot(nx * 0.85, ny);
          let v = 1.35 - d * 1.35 + field(nx, ny, t) * 0.5; // >~0.9 solid, ~0.5 checker, <0.1 empty
          const md = Math.hypot(mouse.x / cell - i, mouse.y / cell - j) / erode;
          if (md < 1.6) v -= Math.exp(-md * md * 2.2) * 1.4;
          if (v > BAYER[j & 3][i & 3]) {
            const x = i * cell + cell / 2, y = j * cell + cell / 2;
            ctx.moveTo(x + cell * 0.4, y);
            ctx.arc(x, y, cell * 0.4, 0, Math.PI * 2);
          }
        }
      }
      ctx.fill();
      if (!still && visible) raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => { target.x = e.offsetX; target.y = e.offsetY; };
    const onLeave = () => { target.x = -1e4; target.y = -1e4; };
    const io = new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (visible) { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); }
    });

    resize();
    raf = requestAnimationFrame(draw);
    io.observe(canvas);
    window.addEventListener('resize', resize);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [cell]);

  return <canvas ref={ref} className={`block w-full h-full ${className}`} aria-hidden="true" />;
}
